import React, { useEffect, useRef, useState } from 'react';
import { SERVER_URL, flushWrites, pauseSync, resumeSync } from '../data/serverStore';

const apiFetch = (path, options = {}) => fetch(`${SERVER_URL}${path}`, {
  credentials: 'include',
  ...options,
});

function fmtDate(value) {
  return value ? new Date(value).toLocaleString() : '—';
}

function fmtSize(value) {
  const bytes = Number(value || 0);
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

function CountCard({ label, value, color = '#1e3060' }) {
  return (
    <div style={{background:'white',border:'1px solid #e0e4ec',borderTop:`3px solid ${color}`,
      borderRadius:7,padding:'11px 14px'}}>
      <div style={{fontSize:17,fontWeight:800,color}}>{value ?? '—'}</div>
      <div style={{fontSize:10,color:'#777',textTransform:'uppercase',marginTop:2}}>{label}</div>
    </div>
  );
}

export default function BackupRestore({ toast }) {
  const fileRef = useRef();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [validated, setValidated] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');

  const loadStatus = async () => {
    try {
      const res = await apiFetch('/api/backups');
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Could not load backup status');
      setStatus(body);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const runBackup = async () => {
    setBusy('Creating a complete backup…');
    setError('');
    try {
      await flushWrites();
      const res = await apiFetch('/api/backups/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'manual admin backup' }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Backup failed');
      toast('success', 'Complete backup created and verified');
      await loadStatus();
      await downloadBackup(body.backup.name);
    } catch (e) {
      setError(e.message);
      toast('error', `Backup failed: ${e.message}`);
    } finally {
      setBusy('');
    }
  };

  const downloadBackup = async (name) => {
    setBusy(`Downloading ${name}…`);
    try {
      const res = await apiFetch(`/api/backups/download/${encodeURIComponent(name)}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Download failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
      toast('error', e.message);
    } finally {
      setBusy('');
    }
  };

  const chooseFile = async (file) => {
    if (!file) return;
    setSelectedFile(file);
    setValidated(null);
    setConfirmation('');
    setError('');
    setBusy('Checking database, files and checksums…');
    const form = new FormData();
    form.append('backup', file);
    try {
      const res = await apiFetch('/api/backups/validate', { method: 'POST', body: form });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Backup validation failed');
      setValidated(body.manifest);
      toast('success', 'Backup is complete and valid');
    } catch (e) {
      setError(e.message);
      toast('error', e.message);
    } finally {
      setBusy('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const restoreBackup = async () => {
    if (!selectedFile || !validated || confirmation !== 'RESTORE') return;
    pauseSync();
    setBusy('Creating safety backup and restoring all records and files…');
    setError('');
    const form = new FormData();
    form.append('backup', selectedFile);
    form.append('confirmation', confirmation);
    try {
      await flushWrites();
      const res = await apiFetch('/api/backups/restore', { method: 'POST', body: form });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Restore failed');
      toast('success', `Restore complete — ${body.counts.samples} samples recovered`);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      resumeSync();
      setError(e.message);
      setBusy('');
      toast('error', `Restore failed safely: ${e.message}`);
    }
  };

  const counts = status?.storage || {};
  const backups = status?.backups || [];

  return (
    <div className="content">
      <div style={{maxWidth:980}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
          <div>
            <h2 style={{margin:0,color:'#1e3060'}}>Complete Backup & Disaster Recovery</h2>
            <div style={{fontSize:11,color:'#777',marginTop:3}}>
              Database, COC images, results sheets, manifest and SHA-256 checksums
            </div>
          </div>
          <button className="btn" onClick={loadStatus} disabled={Boolean(busy)}>↻ Refresh</button>
        </div>

        {error && (
          <div className="notice notice-error" style={{marginBottom:12}}>
            <strong>Backup system:</strong> {error}
          </div>
        )}
        {busy && (
          <div className="notice notice-info" style={{marginBottom:12}}>⏳ {busy}</div>
        )}

        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8,marginBottom:14}}>
          <CountCard label="Storage" value={counts.mode || '—'} color="#1a7dc4"/>
          <CountCard label="Samples" value={counts.samples}/>
          <CountCard label="Results" value={counts.results ?? 'JSON mode'}/>
          <CountCard label="Indexed Files" value={counts.files ?? '—'}/>
          <CountCard label="Offsite" value={status?.offsiteConfigured ? 'Connected' : 'Not configured'}
            color={status?.offsiteConfigured ? '#1a6b42' : '#a06000'}/>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <div className="panel">
            <div className="panel-header">Create Complete Backup</div>
            <div className="panel-body">
              <p style={{fontSize:12,lineHeight:1.6,color:'#555',marginTop:0}}>
                Creates one independently restorable ZIP containing every database key,
                every COC and every raw results sheet. A verified copy is retained locally
                and uploaded offsite when Google Drive is configured.
              </p>
              <button className="btn btn-primary" onClick={runBackup} disabled={Boolean(busy)}
                style={{padding:'9px 20px',fontWeight:700}}>
                Create & Download Full Backup
              </button>
              <div style={{fontSize:10,color:'#777',marginTop:9,lineHeight:1.5}}>
                Hourly database: {status?.retention?.hourly ?? 168} copies ·
                Daily full: {status?.retention?.daily ?? 30} ·
                Monthly archive: {status?.retention?.monthly ?? 120}
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-header" style={{background:'#7b2020'}}>Restore Complete System</div>
            <div className="panel-body">
              <p style={{fontSize:12,lineHeight:1.6,color:'#555',marginTop:0}}>
                The server validates the ZIP and every checksum first. Before changing
                anything, it automatically creates an emergency safety backup.
              </p>
              <input ref={fileRef} type="file" accept=".zip,application/zip"
                onChange={e => chooseFile(e.target.files?.[0])} style={{display:'none'}}/>
              <button className="btn" onClick={() => fileRef.current?.click()} disabled={Boolean(busy)}>
                Choose Full Backup ZIP
              </button>

              {validated && (
                <div style={{marginTop:12,border:'1px solid #b8dac4',background:'#edf9f1',
                  borderRadius:6,padding:'10px 12px',fontSize:11,lineHeight:1.6}}>
                  <strong style={{color:'#1a6b42'}}>✓ Complete backup validated</strong>
                  <div>Created: {fmtDate(validated.createdAt)}</div>
                  <div>
                    {validated.counts?.samples} samples · {validated.counts?.cocFiles} COCs ·
                    {' '}{validated.counts?.resultsSheetFiles} results sheets
                  </div>
                  <label style={{display:'block',marginTop:8,fontWeight:700,color:'#7b2020'}}>
                    Type RESTORE to continue
                  </label>
                  <input value={confirmation} onChange={e => setConfirmation(e.target.value)}
                    style={{width:'100%',marginTop:4,padding:'7px 8px',border:'1px solid #c77',
                      borderRadius:4,fontSize:12}}/>
                  <button onClick={restoreBackup} disabled={confirmation !== 'RESTORE' || Boolean(busy)}
                    style={{marginTop:8,background:'#b83232',color:'white',border:'none',
                      borderRadius:4,padding:'8px 16px',fontWeight:700,cursor:'pointer'}}>
                    Restore Database and All Files
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="panel" style={{marginTop:14}}>
          <div className="panel-header">Available Server Backups</div>
          <div style={{maxHeight:330,overflowY:'auto'}}>
            {backups.length === 0 ? (
              <div style={{padding:18,textAlign:'center',color:'#999',fontSize:12}}>No backups created yet</div>
            ) : backups.map((backup, index) => (
              <div key={backup.name} style={{display:'flex',gap:10,alignItems:'center',
                padding:'9px 12px',borderBottom:'1px solid #eef0f4',
                background:index === 0 ? '#f2f7ff' : 'white'}}>
                <span>{backup.restorable ? '📦' : '🗄️'}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:650,color:'#1e3060',
                    overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{backup.name}</div>
                  <div style={{fontSize:10,color:'#888'}}>
                    {fmtDate(backup.createdAt)} · {fmtSize(backup.size)}
                    {backup.restorable ? ' · Complete restore package' : ' · Database snapshot'}
                  </div>
                </div>
                {backup.restorable && (
                  <button className="btn btn-sm" onClick={() => downloadBackup(backup.name)}
                    disabled={Boolean(busy)}>Download</button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
